import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  Button
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axios from 'axios';
import Key from '../../clientVariables.json';
import { useContactContext } from '../../contexts/ContactContext';

const ContactSearchBar = () => {
  // Get search function and search filters from context
  const { setSearch, searchFilters, isLoading } = useContactContext();
  const [jobTitle, setJobTitle] = useState('');
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);  const [selectedStateId, setSelectedStateId] = useState('');
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedStateName, setSelectedStateName] = useState('');  
  const [selectedCityName, setSelectedCityName] = useState('');
  const [stateSearchText, setStateSearchText] = useState('');
  const [citySearchText, setCitySearchText] = useState('');
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // Fetch US states on component mount
  useEffect(() => {
    fetchStates();
  }, []);

  // Function to fetch US states (country_id 233 is for US)
  const fetchStates = () => {
    setIsLoadingStates(true);
    axios
      .post(`${Key.domain_jnp}/commonAPIs/get_states_by_countryid`, {
        country_id: 233 // US Country ID
      })
      .then((response) => {
        console.log("States API response:", response.data.data);
        
        // Map states from API response
        const stateOptions = response.data.data.map((state) => ({
          value: state.id,
          label: state.name
        }));
        
        setStates(stateOptions);
        setIsLoadingStates(false);
      })
      .catch(error => {
        console.error("Error fetching states:", error);
        setIsLoadingStates(false);
      });
  };

  // Function to fetch cities for a selected state
  const fetchCities = (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    
    setIsLoadingCities(true);
    axios
      .post(`${Key.domain_jnp}/commonAPIs/get_cities_by_stateid`, {
        state_id: stateId
      })
      .then((response) => {
        console.log("City API response:", response.data.data);
        
        // Map cities from API response
        const cityOptions = response.data.data.map((city) => ({
          value: city.id,
          label: city.cityName
        }));
        
        setCities(cityOptions);
        setIsLoadingCities(false);
      })
      .catch(error => {
        console.error("Error fetching cities:", error);
        setIsLoadingCities(false);
        setCities([]);
      });
  };

  const handleStateChange = (event) => {
    const stateId = event.target.value;
    setSelectedStateId(stateId);
    setSelectedCityId('');
    setSelectedCityName('');
    
    // Find the state name from the id for sending in search
    const selectedState = states.find(state => state.value === stateId);
    setSelectedStateName(selectedState ? selectedState.label : '');
    
    // Fetch cities for the selected state
    if (stateId) {
      fetchCities(stateId);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (event) => {
    const cityId = event.target.value;
    setSelectedCityId(cityId);
    
    // Find the city name from the id for sending in search
    const selectedCity = cities.find(city => city.value === cityId);
    setSelectedCityName(selectedCity ? selectedCity.label : '');
  };  // Load filters from context when component mounts or when searchFilters change
  useEffect(() => {
    if (searchFilters) {
      setJobTitle(searchFilters.jobTitle || '');
      setSelectedStateName(searchFilters.state || '');
      setSelectedCityName(searchFilters.city || '');
      setSearchQuery(searchFilters.searchQuery || '');
      setFromDate(searchFilters.fromDate || null);
      setToDate(searchFilters.toDate || null);
      
      // If state and city IDs are available, set them too
      if (typeof searchFilters.state === 'number') {
        setSelectedStateId(searchFilters.state);
        // Get cities for this state
        if (searchFilters.state) {
          fetchCities(searchFilters.state);
        }
      }
      
      if (typeof searchFilters.city === 'number') {
        setSelectedCityId(searchFilters.city);
      }
    }
  }, [searchFilters]);
  const handleSearch = () => {
    setSearch({
      jobTitle,
      state: selectedStateName,
      city: selectedCityName,
      searchQuery,
      fromDate,
      toDate
    });
  };  const handleClear = () => {
    // Clear all form fields
    setJobTitle('');
    setSelectedStateId('');
    setSelectedCityId('');
    setSelectedStateName('');
    setSelectedCityName('');
    setStateSearchText('');
    setCitySearchText('');
    setSearchQuery('');
    setFromDate(null);
    setToDate(null);
    setCities([]);
    
    // Call setSearch with empty object to reset filters in context
    // This will trigger a fetch with no filters
    setSearch({});
  };
  // Handle key press to search on Enter
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };  return (
    <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); handleSearch(); }} sx={{ width: '100%' }}>
      <Grid container spacing={{ xs: 0.5, sm: 1 }} alignItems="flex-start">
        <Grid item xs={12}>
          <Box sx={{ mb: 1 }}>
            <TextField
              fullWidth
              label="Job Title"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="E.g. Software Engineer, Product Manager"
              variant="outlined"
              onKeyDown={handleKeyDown}
              size="small"
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={4} md={2} lg={2}>
          <Autocomplete
            id="state-search-select"
            options={states}
            getOptionLabel={(option) => option.label || ''}
            value={selectedStateId ? states.find(state => state.value === selectedStateId) || null : null}
            onChange={(event, newValue) => {
              setSelectedStateId(newValue ? newValue.value : '');
              setSelectedStateName(newValue ? newValue.label : '');
              setSelectedCityId('');
              setSelectedCityName('');
              if (newValue && newValue.value) {
                fetchCities(newValue.value);
              } else {
                setCities([]);
              }
            }}
            inputValue={stateSearchText}
            onInputChange={(event, newInputValue) => {
              setStateSearchText(newInputValue);
            }}
            loading={isLoadingStates}
            disabled={isLoadingStates}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      {isLoadingStates ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            noOptionsText="No states found"
          />
        </Grid>
          <Grid item xs={12} sm={4} md={2} lg={2}>
          <Autocomplete
            id="city-search-select"
            options={cities}
            getOptionLabel={(option) => option.label || ''}
            value={selectedCityId ? cities.find(city => city.value === selectedCityId) || null : null}
            onChange={(event, newValue) => {
              setSelectedCityId(newValue ? newValue.value : '');
              setSelectedCityName(newValue ? newValue.label : '');
            }}
            inputValue={citySearchText}
            onInputChange={(event, newInputValue) => {
              setCitySearchText(newInputValue);
            }}
            loading={isLoadingCities}
            disabled={!selectedStateId || isLoadingCities}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="City"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      {isLoadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            noOptionsText="No cities found"
          />
        </Grid>
          <Grid item xs={6} sm={4} md={2} lg={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              inputFormat="MM/dd/yyyy"
              PopperProps={{
                sx: { 
                  '& .MuiPaper-root': {
                    transformOrigin: 'top center !important',
                    '&:before': { display: 'none' } 
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        
        <Grid item xs={6} sm={4} md={2} lg={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              inputFormat="MM/dd/yyyy"
              PopperProps={{
                sx: { 
                  '& .MuiPaper-root': {
                    transformOrigin: 'top center !important',
                    '&:before': { display: 'none' } 
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
          <Grid item xs={12} sm={8} md={4} lg={2}>
          <TextField
            fullWidth
            label="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Name, Email, Phone"
            variant="outlined"
            onKeyDown={handleKeyDown}
            size="small"
            InputProps={{
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <Button 
                    size="small" 
                    onClick={() => setSearchQuery('')}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    Clear
                  </Button>
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>
      </Grid>

      <Grid item xs={12} sx={{ mt: { xs: 1, sm: 1 } }}>
        <Grid container spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
          <Grid item xs={12} sm="auto">
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-end', sm: 'flex-end' }
            }}>
              <Button
                variant="outlined"
                onClick={handleClear}
                sx={{ 
                  px: 3,
                  height: '40px',
                  minWidth: '90px',
                  textTransform: 'none',
                  fontWeight: 'normal',
                  borderRadius: '4px',
                  backgroundColor: '#f8f9fa',
                  borderColor: '#ced4da'
                }}
              >
                Clear
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                sx={{ 
                  px: 3,
                  height: '40px',
                  minWidth: '90px',
                  textTransform: 'none',
                  fontWeight: 'normal',
                  borderRadius: '4px'
                }}
              >
                {isLoading ? 'Searching...' : 'Search'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactSearchBar;
